import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { EnforcementStatus, ManagedUser, EnforcementSettings } from './enforcement.types';

@Injectable({ providedIn: 'root' })
export class EnforcementService {
  private readonly apiUrl = environment.apiBase;

  /** Last status fetched — used by the guard for synchronous per-navigation checks. */
  private last?: EnforcementStatus;

  constructor(private http: HttpClient) {}

  /**
   * Current user's lock status. Whitelisted server-side, so it always responds
   * even while locked. Caches the result for the guard's child-route checks.
   */
  getStatus(): Observable<EnforcementStatus> {
    return this.http
      .get<{ statusCode: number; data: EnforcementStatus }>(
        `${this.apiUrl}/board/enforcement/status`
      )
      .pipe(
        map((res) => res.data),
        tap((status) => (this.last = status))
      );
  }

  get cachedStatus(): EnforcementStatus | undefined {
    return this.last;
  }

  /**
   * Request ids a locked user is permitted to open (drawn from the rule blocks).
   * Empty when the user isn't locked.
   */
  allowedRequestIds(status?: EnforcementStatus): number[] {
    const s = status ?? this.last;
    if (!s?.locked) return [];
    return (s.blocks || []).reduce<number[]>(
      (acc, block) => acc.concat((block.items || []).map((i) => i.requestId)),
      []
    );
  }

  // ── Admin (super admin / operation manager) ───────────────────────────────

  /** All lockable users + their lock state, for the access-manager modal. */
  listManagedUsers(): Observable<ManagedUser[]> {
    return this.http
      .get<{ statusCode: number; data: ManagedUser[] }>(
        `${this.apiUrl}/board/enforcement/users`
      )
      .pipe(map((res) => res.data));
  }

  adminLock(userId: number, reason?: string): Observable<unknown> {
    return this.http.post(`${this.apiUrl}/board/enforcement/lock`, { userId, reason });
  }

  adminUnlock(userId: number): Observable<unknown> {
    return this.http.post(`${this.apiUrl}/board/enforcement/unlock`, { userId });
  }

  getEnforcementSettings(): Observable<EnforcementSettings> {
    return this.http
      .get<{ statusCode: number; data: EnforcementSettings }>(
        `${this.apiUrl}/board/enforcement/settings`
      )
      .pipe(map((res) => res.data));
  }

  updateEnforcementSettings(
    dto: Partial<EnforcementSettings>
  ): Observable<EnforcementSettings> {
    return this.http
      .patch<{ statusCode: number; data: EnforcementSettings }>(
        `${this.apiUrl}/board/enforcement/settings`,
        dto
      )
      .pipe(map((res) => res.data));
  }
}
